import React, { useCallback, useEffect, useState } from 'react';
import { DashboardLayout } from '../components/layout';
import { useAdminNavigation } from '../hooks/useAdminNavigation';
import * as adminApi from '../../../api/adminApi';
import { adminListExports, type AuditItem } from '../../../api/reportingApi';
import { S, fmtWhen } from './adminPageStyles';

/**
 * Security console: audit log (SRS #77/#135), security events (#111),
 * blocked IPs (#107/#108) and login events (#94/#126) — the four admin
 * APIs that previously had no screen.
 */

type Tab = 'audit' | 'events' | 'blocks' | 'logins' | 'exports';
const PAGE = 50;

const AdminSecurityPage: React.FC = () => {
  const { sidebarCollapsed, setSidebarCollapsed, activeSection, setSelectedRegulation, handleNavClick, handleLogout } = useAdminNavigation();
  const token = window.localStorage.getItem('policyora_token') ?? '';
  const [tab, setTab] = useState<Tab>('audit');
  const [exports, setExports] = useState<AuditItem[]>([]);
  const [exportFilter, setExportFilter] = useState({ email: '', kind: '' });
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  // audit
  const [audit, setAudit] = useState<adminApi.AuditEntry[]>([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditOffset, setAuditOffset] = useState(0);
  const [auditFilter, setAuditFilter] = useState({ action: '', admin_email: '', target_type: '', since: '' });
  // events
  const [events, setEvents] = useState<adminApi.SecurityEvent[]>([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [eventsOffset, setEventsOffset] = useState(0);
  const [eventFilter, setEventFilter] = useState({ kind: '', ip: '', since: '' });
  // blocks
  const [blocks, setBlocks] = useState<adminApi.BlockedIP[]>([]);
  const [includeExpired, setIncludeExpired] = useState(false);
  const [newBlock, setNewBlock] = useState({ network: '', reason: '', hours: '' });
  // logins
  const [logins, setLogins] = useState<adminApi.LoginEvent[]>([]);
  const [loginsTotal, setLoginsTotal] = useState(0);
  const [loginsOffset, setLoginsOffset] = useState(0);
  const [loginFilter, setLoginFilter] = useState({ email: '', ip: '', since: '' });

  const guard = useCallback(async (fn: () => Promise<void>) => {
    setError(null);
    try { await fn(); } catch (e) { setError(e instanceof Error ? e.message : 'Request failed'); }
  }, []);

  const loadAudit = useCallback(() => guard(async () => {
    const r = await adminApi.getAuditLog(token, { ...auditFilter, limit: PAGE, offset: auditOffset });
    setAudit(r.items); setAuditTotal(r.total);
  }), [token, auditFilter, auditOffset, guard]);
  const loadEvents = useCallback(() => guard(async () => {
    const r = await adminApi.getSecurityEvents(token, { ...eventFilter, limit: PAGE, offset: eventsOffset });
    setEvents(r.items); setEventsTotal(r.total);
  }), [token, eventFilter, eventsOffset, guard]);
  const loadBlocks = useCallback(() => guard(async () => {
    const r = await adminApi.getBlockedIPs(token, includeExpired);
    setBlocks(r.items);
  }), [token, includeExpired, guard]);
  const loadLogins = useCallback(() => guard(async () => {
    const r = await adminApi.getLoginEvents(token, { ...loginFilter, limit: PAGE, offset: loginsOffset });
    setLogins(r.items); setLoginsTotal(r.total);
  }), [token, loginFilter, loginsOffset, guard]);

  useEffect(() => { if (tab === 'audit') loadAudit(); }, [tab, loadAudit]);
  useEffect(() => { if (tab === 'events') loadEvents(); }, [tab, loadEvents]);
  useEffect(() => { if (tab === 'blocks') loadBlocks(); }, [tab, loadBlocks]);
  useEffect(() => { if (tab === 'logins') loadLogins(); }, [tab, loadLogins]);
  const loadExports = useCallback(() => { adminListExports({ email: exportFilter.email || undefined, kind: exportFilter.kind || undefined, limit: 300 }).then((r) => setExports(r.exports)).catch((e) => setError(e instanceof Error ? e.message : 'Failed to load exports')); }, [exportFilter]);
  useEffect(() => { if (tab === 'exports') loadExports(); }, [tab, loadExports]);

  const say = (m: string) => { setFlash(m); setTimeout(() => setFlash(null), 2000); };

  const Pager = ({ total, offset, setOffset }: { total: number; offset: number; setOffset: (n: number) => void }) => (
    <div style={S.pager}>
      <button style={S.btn} disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>← Newer</button>
      <span>{total === 0 ? 'No entries' : `${offset + 1}–${Math.min(offset + PAGE, total)} of ${total}`}</span>
      <button style={S.btn} disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>Older →</button>
    </div>
  );

  return (
    <DashboardLayout collapsed={sidebarCollapsed} activeSection={activeSection} welcomeLabel="Security" selectedRegulation={null}
      onToggleSidebar={() => setSidebarCollapsed((p) => !p)} onNavClick={handleNavClick} onProfileClick={() => handleNavClick('profile')}
      onLogout={handleLogout} onRegulationModalClose={() => setSelectedRegulation(null)}>
      <section style={S.section}>
        <h2 style={S.h2}>Security</h2>
        <p style={S.sub}>Who did what, what the platform refused, which addresses are blocked, and who signed in from where.</p>
        <div style={S.tabs} role="tablist">
          {([['audit', 'Audit log'], ['events', 'Security events'], ['blocks', 'Blocked IPs'], ['logins', 'Login events'], ['exports', 'Customer exports']] as [Tab, string][]).map(([k, l]) => (
            <button key={k} role="tab" aria-selected={tab === k} style={S.tab(tab === k)} onClick={() => setTab(k)}>{l}</button>
          ))}
        </div>
        {error && <div style={S.error}>{error}</div>}
        {flash && <div style={S.ok}>{flash}</div>}

        {tab === 'audit' && (
          <>
            <div style={S.toolbar}>
              <input style={S.input} placeholder="Action (e.g. user.block)" value={auditFilter.action} onChange={(e) => setAuditFilter({ ...auditFilter, action: e.target.value })} />
              <input style={S.input} placeholder="Administrator email" value={auditFilter.admin_email} onChange={(e) => setAuditFilter({ ...auditFilter, admin_email: e.target.value })} />
              <input style={S.input} placeholder="Target type (user, role, ip…)" value={auditFilter.target_type} onChange={(e) => setAuditFilter({ ...auditFilter, target_type: e.target.value })} />
              <input style={S.input} type="date" value={auditFilter.since} onChange={(e) => setAuditFilter({ ...auditFilter, since: e.target.value })} title="Since" />
              <button style={S.primary} onClick={() => { setAuditOffset(0); loadAudit(); }}>Filter</button>
            </div>
            <table style={S.table}><thead><tr><th style={S.th}>When</th><th style={S.th}>Administrator</th><th style={S.th}>Action</th><th style={S.th}>Target</th><th style={S.th}>Detail</th><th style={S.th}>IP</th></tr></thead>
              <tbody>{audit.map((a) => (
                <tr key={a.id}><td style={S.td}>{fmtWhen(a.created_at)}</td><td style={S.td}>{a.admin_email}</td><td style={{ ...S.td, ...S.mono }}>{a.action}</td>
                  <td style={S.td}>{a.target_type ? <span style={S.chip}>{a.target_type}</span> : null} {a.target_label || a.target_id || ''}</td>
                  <td style={{ ...S.td, ...S.mono, maxWidth: 360, wordBreak: 'break-word' }}>{a.detail ? JSON.stringify(a.detail) : ''}</td>
                  <td style={{ ...S.td, ...S.mono }}>{a.ip_address || ''}</td></tr>
              ))}</tbody></table>
            <Pager total={auditTotal} offset={auditOffset} setOffset={setAuditOffset} />
          </>
        )}

        {tab === 'events' && (
          <>
            <div style={S.toolbar}>
              <select style={S.input} value={eventFilter.kind} onChange={(e) => setEventFilter({ ...eventFilter, kind: e.target.value })}>
                <option value="">All kinds</option>
                {['login.failed', 'ratelimit.exceeded', 'captcha.failed', 'ip.blocked', 'ip.auto_blocked'].map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
              <input style={S.input} placeholder="IP address" value={eventFilter.ip} onChange={(e) => setEventFilter({ ...eventFilter, ip: e.target.value })} />
              <input style={S.input} type="date" value={eventFilter.since} onChange={(e) => setEventFilter({ ...eventFilter, since: e.target.value })} title="Since" />
              <button style={S.primary} onClick={() => { setEventsOffset(0); loadEvents(); }}>Filter</button>
            </div>
            <table style={S.table}><thead><tr><th style={S.th}>When</th><th style={S.th}>Kind</th><th style={S.th}>IP</th><th style={S.th}>Path</th><th style={S.th}>Detail</th><th style={S.th} /></tr></thead>
              <tbody>{events.map((ev) => (
                <tr key={ev.id}><td style={S.td}>{fmtWhen(ev.created_at)}</td><td style={{ ...S.td, ...S.mono }}>{ev.kind}</td><td style={{ ...S.td, ...S.mono }}>{ev.ip_address || ''}</td>
                  <td style={{ ...S.td, ...S.mono }}>{ev.path || ''}</td><td style={{ ...S.td, ...S.mono, maxWidth: 360, wordBreak: 'break-word' }}>{ev.detail ? JSON.stringify(ev.detail) : ''}</td>
                  <td style={S.td}>{ev.ip_address && <button style={S.danger} onClick={() => { setNewBlock({ network: ev.ip_address!, reason: `From security event ${ev.kind}`, hours: '24' }); setTab('blocks'); }}>Block…</button>}</td></tr>
              ))}</tbody></table>
            <Pager total={eventsTotal} offset={eventsOffset} setOffset={setEventsOffset} />
          </>
        )}

        {tab === 'blocks' && (
          <>
            <form style={S.toolbar} onSubmit={(e) => { e.preventDefault(); guard(async () => {
              await adminApi.blockIP(token, { network: newBlock.network.trim(), reason: newBlock.reason.trim(), hours: newBlock.hours ? Number(newBlock.hours) : null });
              setNewBlock({ network: '', reason: '', hours: '' }); say('Blocked'); await loadBlocks();
            }); }}>
              <input style={S.input} placeholder="IP or CIDR (203.0.113.0/24)" value={newBlock.network} onChange={(e) => setNewBlock({ ...newBlock, network: e.target.value })} required />
              <input style={{ ...S.input, minWidth: 260 }} placeholder="Reason" value={newBlock.reason} onChange={(e) => setNewBlock({ ...newBlock, reason: e.target.value })} required />
              <input style={{ ...S.input, minWidth: 110 }} placeholder="Hours (blank = permanent)" value={newBlock.hours} onChange={(e) => setNewBlock({ ...newBlock, hours: e.target.value })} />
              <button style={S.primary} type="submit">Block</button>
              <label style={{ marginLeft: 'auto', fontSize: '0.84rem', color: 'var(--ds-text-muted)' }}><input type="checkbox" checked={includeExpired} onChange={(e) => setIncludeExpired(e.target.checked)} /> show expired</label>
            </form>
            <table style={S.table}><thead><tr><th style={S.th}>Network</th><th style={S.th}>Reason</th><th style={S.th}>Source</th><th style={S.th}>By</th><th style={S.th}>Created</th><th style={S.th}>Expires</th><th style={S.th}>Hits</th><th style={S.th} /></tr></thead>
              <tbody>{blocks.length === 0 && <tr><td style={{ ...S.td, ...S.muted }} colSpan={8}>No blocked addresses.</td></tr>}
              {blocks.map((b) => (
                <tr key={b.id}><td style={{ ...S.td, ...S.mono }}>{b.network}</td><td style={S.td}>{b.reason}</td><td style={S.td}><span style={S.chip}>{b.source}</span></td>
                  <td style={S.td}>{b.created_by || '—'}</td><td style={S.td}>{fmtWhen(b.created_at)}</td><td style={S.td}>{b.expires_at ? fmtWhen(b.expires_at) : 'never'}</td>
                  <td style={S.td}>{b.hits}{b.last_hit_at ? <span style={S.muted}> · last {fmtWhen(b.last_hit_at)}</span> : null}</td>
                  <td style={S.td}><button style={S.danger} onClick={() => guard(async () => { await adminApi.unblockIP(token, b.id); say('Unblocked'); await loadBlocks(); })}>Unblock</button></td></tr>
              ))}</tbody></table>
          </>
        )}

        {tab === 'exports' && (
          <>
            <p style={S.sub}>Every file that left the platform from a customer account (SRS #128): team exports, usage reports, invoices, contracts, generated reports, conversations, documents.</p>
            <div style={S.toolbar}>
              <input style={S.input} placeholder="Person (email)" value={exportFilter.email} onChange={(e) => setExportFilter({ ...exportFilter, email: e.target.value })} />
              <select style={S.input} value={exportFilter.kind} onChange={(e) => setExportFilter({ ...exportFilter, kind: e.target.value })}>
                <option value="">Any kind</option>{['team', 'usage', 'audit', 'invoice', 'contract', 'report', 'chat', 'document'].map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
              <button style={S.btn} onClick={loadExports}>Refresh</button>
            </div>
            <table style={S.table}><thead><tr><th style={S.th}>When</th><th style={S.th}>Organisation</th><th style={S.th}>Person</th><th style={S.th}>What</th><th style={S.th}>Target</th><th style={S.th}>IP</th></tr></thead>
              <tbody>
                {exports.length === 0 && <tr><td style={S.td} colSpan={6}><span style={S.muted}>No exports recorded.</span></td></tr>}
                {exports.map((x) => (
                  <tr key={x.id}><td style={S.td}>{fmtWhen(x.created_at)}</td><td style={S.td}>{x.organisation || x.owner_email}</td><td style={S.td}>{x.actor || '—'}</td>
                    <td style={S.td}>{x.label}{x.detail?.format ? <span style={{ ...S.chip, marginLeft: 6 }}>{String(x.detail.format)}</span> : null}</td>
                    <td style={S.td}>{x.target_label || x.target_id || '—'}</td><td style={{ ...S.td, ...S.mono }}>{x.ip_address || '—'}</td></tr>
                ))}
              </tbody></table>
          </>
        )}

        {tab === 'logins' && (
          <>
            <div style={S.toolbar}>
              <input style={S.input} placeholder="User email" value={loginFilter.email} onChange={(e) => setLoginFilter({ ...loginFilter, email: e.target.value })} />
              <input style={S.input} placeholder="IP address" value={loginFilter.ip} onChange={(e) => setLoginFilter({ ...loginFilter, ip: e.target.value })} />
              <input style={S.input} type="date" value={loginFilter.since} onChange={(e) => setLoginFilter({ ...loginFilter, since: e.target.value })} title="Since" />
              <button style={S.primary} onClick={() => { setLoginsOffset(0); loadLogins(); }}>Filter</button>
            </div>
            <table style={S.table}><thead><tr><th style={S.th}>Signed in</th><th style={S.th}>User</th><th style={S.th}>IP</th><th style={S.th}>Device</th><th style={S.th}>Method</th><th style={S.th}>Signed out</th></tr></thead>
              <tbody>{logins.map((l) => (
                <tr key={l.id}><td style={S.td}>{fmtWhen(l.login_at)}</td><td style={S.td}>{l.email || '—'}</td><td style={{ ...S.td, ...S.mono }}>{l.ip_address || <span style={S.muted}>not recorded</span>}</td>
                  <td style={S.td} title={l.user_agent || ''}>{l.device || <span style={S.muted}>—</span>}</td><td style={S.td}>{l.method || '—'}</td><td style={S.td}>{l.logout_at ? fmtWhen(l.logout_at) : <span style={S.muted}>open</span>}</td></tr>
              ))}</tbody></table>
            <Pager total={loginsTotal} offset={loginsOffset} setOffset={setLoginsOffset} />
          </>
        )}
      </section>
    </DashboardLayout>
  );
};

export default AdminSecurityPage;
